// src/components/reports/Reports.js
import React from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, ResponsiveGrid } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { TRUCK_STATUSES } from '../../data/store';

export default function Reports() {
  const { trucks, documents, clients, gpsDevices } = useApp();
  const { isMobile } = useResponsive();

  const trucksByStatus = Object.entries(TRUCK_STATUSES).map(([key, s]) => ({
    name: s.label,
    count: trucks.filter(t => t.status === key).length,
    color: s.color,
  }));

  const docsByStatus = [
    { name: 'Pending', count: documents.filter(d => d.status === 'pending').length, color: '#f59e0b' },
    { name: 'In Transit', count: documents.filter(d => d.status === 'in-transit').length, color: '#3b82f6' },
    { name: 'Delivered', count: documents.filter(d => d.status === 'delivered').length, color: '#10b981' },
    { name: 'Cancelled', count: documents.filter(d => d.status === 'cancelled').length, color: '#ef4444' },
  ];

  const clientTruckData = clients.map(c => ({
    name: c.name.split(' ')[0],
    trucks: trucks.filter(t => t.clientId === c.id).length,
  }));

  const fuelData = trucks.map(t => ({
    name: t.plateNumber,
    fuel: t.fuelLevel,
    color: t.fuelLevel > 50 ? '#10b981' : t.fuelLevel > 25 ? '#f59e0b' : '#ef4444',
  }));

  const weeklyActivity = [
    { day: 'Mon', trips: 12, delivered: 10, pending: 2 },
    { day: 'Tue', trips: 18, delivered: 15, pending: 3 },
    { day: 'Wed', trips: 14, delivered: 12, pending: 2 },
    { day: 'Thu', trips: 22, delivered: 19, pending: 3 },
    { day: 'Fri', trips: 26, delivered: 21, pending: 5 },
    { day: 'Sat', trips: 15, delivered: 14, pending: 1 },
    { day: 'Sun', trips: 8, delivered: 8, pending: 0 },
  ];

  const tooltipStyle = { background: 'var(--bgCard)', border: '1px solid var(--borderInput)', borderRadius: 8, color: 'var(--textPrimary)', fontSize: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
        <Card title="Fleet Status Distribution">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={trucksByStatus} cx="50%" cy="50%" outerRadius={80} dataKey="count" label={({ name, count }) => count > 0 ? `${name}: ${count}` : ''} labelLine={false} fontSize={11}>
                {trucksByStatus.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Document Status">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={docsByStatus} barSize={32}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2340" />
              <XAxis dataKey="name" tick={{ fill: 'var(--textMuted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--textMuted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {docsByStatus.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Weekly Trip Activity">
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={weeklyActivity}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1a2340" />
            <XAxis dataKey="day" tick={{ fill: 'var(--textMuted)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'var(--textMuted)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Line type="monotone" dataKey="trips" stroke="#f97316" strokeWidth={2.5} dot={{ fill: '#f97316', r: 4 }} name="Total Trips" />
            <Line type="monotone" dataKey="delivered" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 3 }} name="Delivered" />
            <Line type="monotone" dataKey="pending" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" dot={{ fill: '#f59e0b', r: 3 }} name="Pending" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
        <Card title="Trucks per Client">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={clientTruckData} barSize={28} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2340" />
              <XAxis type="number" tick={{ fill: 'var(--textMuted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis dataKey="name" type="category" tick={{ fill: 'var(--textMuted)', fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="trucks" fill="#3b82f6" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Fuel Levels">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={fuelData} barSize={24}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2340" />
              <XAxis dataKey="name" tick={{ fill: 'var(--textMuted)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--textMuted)', fontSize: 11 }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" />
              <Tooltip contentStyle={tooltipStyle} formatter={v => [`${v}%`, 'Fuel']} />
              <Bar dataKey="fuel" radius={[6, 6, 0, 0]}>
                {fuelData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Summary Statistics">
        <ResponsiveGrid columns={5} tabletColumns={3} mobileColumns={2} gap={16}>
          {[
            { label: 'Total Fleet', value: trucks.length, color: '#3b82f6' },
            { label: 'Active Clients', value: clients.filter(c => c.status === 'active').length, color: '#10b981' },
            { label: 'GPS Devices', value: gpsDevices.length, color: '#8b5cf6' },
            { label: 'Total Docs', value: documents.length, color: '#f97316' },
            { label: 'Delivery Rate', value: `${documents.length > 0 ? Math.round(documents.filter(d => d.status === 'delivered').length / documents.length * 100) : 0}%`, color: '#f59e0b' },
          ].map(item => (
            <div key={item.label} style={{ textAlign: 'center', padding: '16px 12px', background: 'var(--bgInput)', borderRadius: 10, border: `1px solid ${item.color}22` }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 28, color: item.color }}>{item.value}</div>
              <div style={{ fontSize: 11, color: 'var(--textMuted)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</div>
            </div>
          ))}
        </ResponsiveGrid>
      </Card>
    </div>
  );
}
